import React from 'react';
import { Form, ICON_MAGNIFY, Label } from '@doveit/lego';
import merge from 'lodash.merge';
import {
  PlaceType, Place, suggestPlace, SuggestPlaceResponse,
} from '../../../services/place/place';
import SearchPlaceModal from '../../containers/search-place-modal/SearchPlaceModal';
import * as styles from './PropertyFilterForm.style';
import SelectableItem from '../selectable-item/SelectableItem';
import mapToSelectableItemProps from '../../utils/place/mapToSelectableItemProps';
import {
  defaultFieldsConfig,
} from './fieldsPreset';
import { FormFieldsConfig } from '../../types';

export const DEFAULT_MAX_SUGGESTIONS = 10;

const printPlace = (place: Place) => {
  switch (place.type) {
    case PlaceType.PROVINCE:
      return `${place.name} - Provincia`;
    case PlaceType.LOCALITY:
      return `${place.name} - Comune`;
    case PlaceType.AREA:
      return `${place.name} - ${place.parent ? place.parent.name : 'Quartiere'}`;
    default:
      throw new Error('Unreachable');
  }
};

const fetchSuggestions = (value: string) => suggestPlace(value, { size: DEFAULT_MAX_SUGGESTIONS });

const renderSuggestion = (suggestion: SuggestPlaceResponse) => (
  <SelectableItem
    {...mapToSelectableItemProps(suggestion)}
    noPad
  />
);

export interface PropertyFilterFormModel {
  place?: Place,
  price?: {
    min?: string,
    max?: string,
  },
  size?: {
    min?: string,
    max?: string,
  },
}

export interface PropertyFilterFormProps {
  initialValues?: PropertyFilterFormModel,
  fields?: FormFieldsConfig,
  onSubmit: (values: PropertyFilterFormModel) => void,
  loading?: boolean,
}

const isVisible = (obj) => !obj.hidden;

const DEFAULT_INITIAL_VALUES = { price: { min: '', max: '' }, size: { min: '', max: '' } };

const PropertyFilterForm: React.FC<PropertyFilterFormProps> = ({
  initialValues,
  fields = {},
  onSubmit,
  loading,
}) => {
  const fieldsMerged = merge({}, defaultFieldsConfig, fields);

  const mergedInitialValues = React.useMemo(
    () => merge(DEFAULT_INITIAL_VALUES, initialValues || {}),
    [initialValues],
  );

  return (
    <div style={{ minHeight: '22rem' }}>
      <Form
        initialValues={mergedInitialValues}
        onSubmit={onSubmit}
        preventEnterKeypress
        enableReinitialize
        loading={loading}
      >
        {({ values, handleChange }) => {
          const onPlaceSelected = (place: Place) => {
            handleChange({ target: { name: 'place', value: place } });
          };

          return (
            <>
              {isVisible(fieldsMerged.place) && (
                <Form.Item>
                  <div css={styles.autoCompleteDesktop}>
                    <Form.AutoComplete
                      id="place"
                      name="place"
                      minLength={2}
                      icon={ICON_MAGNIFY}
                      label={fieldsMerged.place.label}
                      aria-label={fieldsMerged.place.ariaLabel}
                      placeholder={fieldsMerged.place.placeholder}
                      suggestions={fetchSuggestions}
                      onChange={() => handleChange({ target: { name: 'place', value: undefined } })}
                      printSuggestion={(suggestion) => printPlace(suggestion.place)}
                      value={values.place ? printPlace(values.place) : undefined}
                      onSuggestionSelected={(suggestion) => onPlaceSelected(suggestion.place)}
                      render={{ suggestion: renderSuggestion }}
                    />
                  </div>
                  <div css={styles.autoCompleteMobile}>
                    <Label text={fieldsMerged.place.label} />
                    <SearchPlaceModal
                      initialValue={values.place ? printPlace(values.place) : undefined}
                      onPlaceSelected={onPlaceSelected}
                      loading={loading}
                      field={{
                        label: fieldsMerged.place.label,
                        placeholder: fieldsMerged.place.placeholder,
                      }}
                    />
                  </div>
                </Form.Item>
              )}
              {isVisible(fieldsMerged.price) && (
                <Form.Item>
                  <Form.PriceRange
                    name="price"
                    label={fieldsMerged.price.label}
                    maxVisibleSuggestions={3}
                  />
                </Form.Item>
              )}
              {isVisible(fieldsMerged.size) && (
                <Form.Item>
                  <Form.SizeRange
                    name="size"
                    label={fieldsMerged.size.label}
                    maxVisibleSuggestions={3}
                  />
                </Form.Item>
              )}
              <Form.Item>
                <Form.Submit
                  label={fieldsMerged.button.label}
                  aria-label={fieldsMerged.button.ariaLabel}
                  expanded
                />
              </Form.Item>
            </>
          );
        }}
      </Form>
    </div>
  );
};

export default PropertyFilterForm;
